import React, { useRef } from 'react';
import { 
  Download, Printer, Coffee, Users, Gift, TrendingUp, 
  QrCode, MessageCircle, MapPin, Clock, Award, Crown, Star
} from 'lucide-react';
import { Button } from '@/components/ui/button';

/**
 * Sales Rep Guide - Printable PDF-ready page
 * Access via /sales-rep-guide
 */
export default function SalesRepGuidePage() {
  const printRef = useRef(null);

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Print Button - Hidden when printing */}
      <div className="print:hidden fixed top-4 right-4 z-50 flex gap-2">
        <Button onClick={handlePrint} size="lg" className="shadow-lg">
          <Printer className="w-5 h-5 mr-2" />
          Print / Save as PDF
        </Button>
      </div>

      {/* Printable Content */}
      <div 
        ref={printRef}
        className="max-w-4xl mx-auto bg-white shadow-xl print:shadow-none print:max-w-none"
        style={{ fontFamily: 'Georgia, serif' }}
      >
        {/* Cover Page */}
        <div className="min-h-screen flex flex-col justify-center items-center p-12 bg-gradient-to-br from-green-700 to-green-900 text-white print:break-after-page">
          <div className="text-center">
            <h1 className="text-5xl font-bold mb-4">BULACCINO CAFE</h1>
            <div className="w-24 h-1 bg-amber-400 mx-auto mb-8"></div>
            <h2 className="text-3xl font-light mb-2">Partner Program</h2>
            <h3 className="text-2xl font-bold text-amber-300">Sales Representative Guide</h3>
            <div className="mt-12 text-lg opacity-80">
              <p>Your Complete Guide to Building Our Partner Network</p>
              <p className="mt-4">Martintar, Nadi, Fiji</p>
            </div>
          </div>
        </div>

        {/* Page 1: Your Role */}
        <div className="p-12 print:break-after-page">
          <h2 className="text-3xl font-bold text-green-800 mb-6 border-b-2 border-green-200 pb-2">
            Your Role as a Sales Representative
          </h2>
          
          <p className="text-lg text-gray-700 mb-6 leading-relaxed">
            You're helping Bulaccino Cafe build a network of <strong>taxi drivers</strong>, <strong>tour bus operators</strong>, 
            and <strong>hotel concierge staff</strong> who refer tourists to our cafe. When these partners refer tourists, 
            they earn free coffee and lifetime discounts!
          </p>

          <div className="bg-amber-50 border-l-4 border-amber-500 p-6 mb-8">
            <h3 className="font-bold text-xl text-amber-800 mb-2">Your Mission</h3>
            <p className="text-gray-700">
              Sign up as many partners as possible using your unique QR code. The more partners you enroll, 
              the more tourists visit Bulaccino Cafe!
            </p>
          </div>

          <h3 className="text-2xl font-bold text-green-800 mb-4">Your Unique Materials</h3>
          
          <div className="grid grid-cols-2 gap-6 mb-8">
            <div className="bg-gray-50 p-6 rounded-lg border">
              <div className="flex items-center gap-3 mb-3">
                <QrCode className="w-8 h-8 text-green-600" />
                <h4 className="font-bold text-lg">Your QR Code</h4>
              </div>
              <p className="text-gray-600">
                A scannable QR code that takes partners directly to the signup page with your attribution.
              </p>
            </div>
            <div className="bg-gray-50 p-6 rounded-lg border">
              <div className="flex items-center gap-3 mb-3">
                <MessageCircle className="w-8 h-8 text-green-600" />
                <h4 className="font-bold text-lg">Your Signup Link</h4>
              </div>
              <p className="text-gray-600 font-mono text-sm">
                bulaccinocafe.com/partner-signup?rep=<span className="text-green-600">[YOUR-CODE]</span>
              </p>
            </div>
          </div>

          <div className="bg-green-50 p-6 rounded-lg">
            <h4 className="font-bold text-lg text-green-800 mb-3">How to Get Your Materials</h4>
            <p className="text-gray-700">
              Your manager will provide you with your unique code, QR code image, and signup link. 
              Save the QR code to your phone so you can show it to potential partners anytime.
            </p>
          </div>
        </div>

        {/* Page 2: What Partners Earn */}
        <div className="p-12 print:break-after-page">
          <h2 className="text-3xl font-bold text-green-800 mb-6 border-b-2 border-green-200 pb-2">
            What Partners Earn
          </h2>

          <p className="text-lg text-gray-700 mb-8">
            This is what you'll be explaining to drivers, concierge, and tour operators. 
            These benefits make it easy to get people excited about joining!
          </p>

          <div className="grid grid-cols-2 gap-8 mb-8">
            <div className="bg-amber-50 p-6 rounded-xl border-2 border-amber-200">
              <div className="flex items-center gap-3 mb-4">
                <Coffee className="w-10 h-10 text-amber-600" />
                <h3 className="text-xl font-bold text-amber-800">Free Coffee</h3>
              </div>
              <p className="text-3xl font-bold text-amber-600 mb-2">1 Free Coffee</p>
              <p className="text-gray-600">For every 100 FJD in tourist purchases using their code</p>
              <div className="mt-4 text-sm text-gray-500 bg-white p-3 rounded">
                <strong>Example:</strong> 20 tourists × 5 FJD average = 1 free coffee!
              </div>
            </div>

            <div className="bg-green-50 p-6 rounded-xl border-2 border-green-200">
              <div className="flex items-center gap-3 mb-4">
                <Gift className="w-10 h-10 text-green-600" />
                <h3 className="text-xl font-bold text-green-800">Tourist Discount</h3>
              </div>
              <p className="text-3xl font-bold text-green-600 mb-2">5% OFF</p>
              <p className="text-gray-600">Every tourist who uses their code saves 5%</p>
              <div className="mt-4 text-sm text-gray-500 bg-white p-3 rounded">
                <strong>Win-Win:</strong> Tourists save money, partners earn rewards!
              </div>
            </div>
          </div>

          <h3 className="text-2xl font-bold text-green-800 mb-4">Partner Discount Tiers</h3>
          <p className="text-gray-600 mb-6">As tourists spend more, partners unlock bigger discounts for themselves:</p>

          <div className="grid grid-cols-4 gap-4">
            <div className="bg-amber-100 p-4 rounded-lg text-center border-2 border-amber-300">
              <Award className="w-8 h-8 text-amber-700 mx-auto mb-2" />
              <h4 className="font-bold text-amber-800">Bronze</h4>
              <p className="text-2xl font-bold text-amber-700">10%</p>
              <p className="text-sm text-gray-600">At 1,000 FJD</p>
            </div>
            <div className="bg-gray-200 p-4 rounded-lg text-center border-2 border-gray-400">
              <Award className="w-8 h-8 text-gray-600 mx-auto mb-2" />
              <h4 className="font-bold text-gray-700">Silver</h4>
              <p className="text-2xl font-bold text-gray-700">12.5%</p>
              <p className="text-sm text-gray-600">At 2,000 FJD</p>
            </div>
            <div className="bg-yellow-100 p-4 rounded-lg text-center border-2 border-yellow-400">
              <Star className="w-8 h-8 text-yellow-600 mx-auto mb-2" />
              <h4 className="font-bold text-yellow-700">Gold</h4>
              <p className="text-2xl font-bold text-yellow-700">15%</p>
              <p className="text-sm text-gray-600">At 3,000 FJD</p>
            </div>
            <div className="bg-purple-100 p-4 rounded-lg text-center border-2 border-purple-400">
              <Crown className="w-8 h-8 text-purple-600 mx-auto mb-2" />
              <h4 className="font-bold text-purple-700">Platinum</h4>
              <p className="text-2xl font-bold text-purple-700">20%</p>
              <p className="text-sm text-gray-600">At 5,000 FJD</p>
            </div>
          </div>

          <p className="text-center text-gray-500 mt-4 italic">
            These discounts are for life! Once earned, they never expire.
          </p>
        </div>

        {/* Page 3: The Sales Script */}
        <div className="p-12 print:break-after-page">
          <h2 className="text-3xl font-bold text-green-800 mb-6 border-b-2 border-green-200 pb-2">
            What to Say (Sales Script)
          </h2>

          <div className="space-y-6">
            <div className="bg-blue-50 p-6 rounded-lg border-l-4 border-blue-500">
              <h3 className="font-bold text-blue-800 mb-2">Step 1: Opening</h3>
              <p className="text-gray-700 italic text-lg">
                "Bula! I'm with Bulaccino Cafe - the best coffee spot near Nadi Airport. 
                We have a partner program for people like you who interact with tourists every day."
              </p>
            </div>

            <div className="bg-green-50 p-6 rounded-lg border-l-4 border-green-500">
              <h3 className="font-bold text-green-800 mb-2">Step 2: Explain Benefits</h3>
              <p className="text-gray-700 italic text-lg">
                "Here's how it works: You get a unique code to share with tourists. 
                Tourists get 5% off when they use your code. You earn 1 free coffee for every 100 FJD tourists spend. 
                At 1,000 FJD in tourist spending, you unlock 10% off for yourself - for life! 
                It keeps going up to 20% off at 5,000 FJD."
              </p>
            </div>

            <div className="bg-amber-50 p-6 rounded-lg border-l-4 border-amber-500">
              <h3 className="font-bold text-amber-800 mb-2">Step 3: Close</h3>
              <p className="text-gray-700 italic text-lg">
                "It takes 30 seconds to sign up - just scan this QR code with your phone camera. 
                It's completely free to join!"
              </p>
              <p className="text-gray-600 mt-2">[Show them your QR code]</p>
            </div>
          </div>

          <div className="mt-8 bg-gray-100 p-6 rounded-lg">
            <h3 className="font-bold text-lg mb-3">Handling Objections</h3>
            <div className="space-y-3 text-gray-700">
              <p><strong>"I don't have time"</strong> → "It's just 30 seconds - scan, fill in name and phone, done!"</p>
              <p><strong>"I don't talk to many tourists"</strong> → "Even a few tourists a week adds up. Free coffee is free coffee!"</p>
              <p><strong>"How do I know it works?"</strong> → "You get a text every time a tourist uses your code. You can track everything online."</p>
            </div>
          </div>
        </div>

        {/* Page 4: How It Works for Partners */}
        <div className="p-12 print:break-after-page">
          <h2 className="text-3xl font-bold text-green-800 mb-6 border-b-2 border-green-200 pb-2">
            How Partners Use the Program
          </h2>

          <p className="text-lg text-gray-700 mb-6">
            Once a partner signs up, here's what they do:
          </p>

          <div className="space-y-6">
            <div className="flex gap-4 items-start">
              <div className="w-10 h-10 rounded-full bg-green-600 text-white flex items-center justify-center font-bold flex-shrink-0">1</div>
              <div>
                <h3 className="font-bold text-lg">Get Their Unique Code</h3>
                <p className="text-gray-600">After signing up, they receive a code like <code className="bg-gray-100 px-2 py-1 rounded">BULA-XXXX</code></p>
              </div>
            </div>

            <div className="flex gap-4 items-start">
              <div className="w-10 h-10 rounded-full bg-green-600 text-white flex items-center justify-center font-bold flex-shrink-0">2</div>
              <div>
                <h3 className="font-bold text-lg">Download Tourist Voucher</h3>
                <p className="text-gray-600">They get a beautiful voucher image to save to their phone and share with tourists</p>
              </div>
            </div>

            <div className="flex gap-4 items-start">
              <div className="w-10 h-10 rounded-full bg-green-600 text-white flex items-center justify-center font-bold flex-shrink-0">3</div>
              <div>
                <h3 className="font-bold text-lg">Share with Tourists</h3>
                <p className="text-gray-600">Show the voucher, text it, or just tell tourists the code</p>
              </div>
            </div>

            <div className="flex gap-4 items-start">
              <div className="w-10 h-10 rounded-full bg-green-600 text-white flex items-center justify-center font-bold flex-shrink-0">4</div>
              <div>
                <h3 className="font-bold text-lg">Track Earnings</h3>
                <p className="text-gray-600">Partners can check their dashboard anytime at bulaccinocafe.com/partner</p>
              </div>
            </div>
          </div>

          <div className="mt-8 bg-green-50 p-6 rounded-lg">
            <h3 className="font-bold text-lg text-green-800 mb-3">What Partners Tell Tourists</h3>
            <p className="text-gray-700 italic text-lg">
              "I have a code that gets you 5% off at Bulaccino Cafe - the best coffee in Nadi! 
              Just tell them <strong>BULA-XXXX</strong> when you order."
            </p>
          </div>

          <div className="mt-6 bg-blue-50 p-6 rounded-lg">
            <h3 className="font-bold text-lg text-blue-800 mb-3">How Tourists Redeem</h3>
            <div className="grid grid-cols-2 gap-4 text-gray-700">
              <div>
                <p className="font-semibold">At the Kiosk:</p>
                <p>Enter code at checkout screen</p>
              </div>
              <div>
                <p className="font-semibold">At the Counter:</p>
                <p>Say "I have partner code BULA-XXXX"</p>
              </div>
            </div>
          </div>
        </div>

        {/* Page 5: Tips for Success */}
        <div className="p-12 print:break-after-page">
          <h2 className="text-3xl font-bold text-green-800 mb-6 border-b-2 border-green-200 pb-2">
            Tips for Success
          </h2>

          <div className="grid grid-cols-2 gap-6">
            <div className="bg-green-50 p-5 rounded-lg">
              <h3 className="font-bold text-green-800 mb-2">✅ Best Targets</h3>
              <ul className="text-gray-700 space-y-2">
                <li>• Taxi ranks near hotels & airport</li>
                <li>• Hotel front desks & concierge</li>
                <li>• Tour bus companies</li>
                <li>• Resort activity desks</li>
                <li>• Car rental counters</li>
              </ul>
            </div>

            <div className="bg-blue-50 p-5 rounded-lg">
              <h3 className="font-bold text-blue-800 mb-2">✅ Best Times</h3>
              <ul className="text-gray-700 space-y-2">
                <li>• Early morning (before tourists arrive)</li>
                <li>• Slow periods at taxi ranks</li>
                <li>• Staff shift changes at hotels</li>
                <li>• End of day when drivers rest</li>
              </ul>
            </div>

            <div className="bg-amber-50 p-5 rounded-lg">
              <h3 className="font-bold text-amber-800 mb-2">✅ Key Points to Emphasize</h3>
              <ul className="text-gray-700 space-y-2">
                <li>• FREE coffee (everyone loves free!)</li>
                <li>• Takes 30 seconds to sign up</li>
                <li>• Lifetime discounts never expire</li>
                <li>• Tourists save too (win-win)</li>
              </ul>
            </div>

            <div className="bg-purple-50 p-5 rounded-lg">
              <h3 className="font-bold text-purple-800 mb-2">✅ Follow-Up</h3>
              <ul className="text-gray-700 space-y-2">
                <li>• Ask if they signed up successfully</li>
                <li>• Remind them to download the voucher</li>
                <li>• Check back in a week</li>
                <li>• Celebrate their first referral!</li>
              </ul>
            </div>
          </div>

          <div className="mt-8 bg-red-50 border-2 border-red-200 p-6 rounded-lg">
            <h3 className="font-bold text-red-800 mb-2">⚠️ Important Reminders</h3>
            <ul className="text-gray-700 space-y-2">
              <li>• Always be friendly and professional - you represent Bulaccino Cafe</li>
              <li>• Never pressure anyone - if they're not interested, thank them and move on</li>
              <li>• Make sure they scan YOUR QR code so you get credit</li>
              <li>• Report any issues to your manager immediately</li>
            </ul>
          </div>
        </div>

        {/* Final Page: Quick Reference */}
        <div className="p-12 bg-gray-50">
          <h2 className="text-3xl font-bold text-green-800 mb-6 border-b-2 border-green-200 pb-2">
            Quick Reference Card
          </h2>

          <div className="grid grid-cols-2 gap-8">
            <div>
              <h3 className="font-bold text-lg text-green-800 mb-3">Partner Benefits</h3>
              <table className="w-full text-sm">
                <tbody>
                  <tr className="border-b"><td className="py-2">Free Coffee</td><td className="py-2 font-bold">1 per 100 FJD</td></tr>
                  <tr className="border-b"><td className="py-2">Bronze (10% off)</td><td className="py-2 font-bold">1,000 FJD</td></tr>
                  <tr className="border-b"><td className="py-2">Silver (12.5% off)</td><td className="py-2 font-bold">2,000 FJD</td></tr>
                  <tr className="border-b"><td className="py-2">Gold (15% off)</td><td className="py-2 font-bold">3,000 FJD</td></tr>
                  <tr><td className="py-2">Platinum (20% off)</td><td className="py-2 font-bold">5,000 FJD</td></tr>
                </tbody>
              </table>
            </div>

            <div>
              <h3 className="font-bold text-lg text-green-800 mb-3">Cafe Information</h3>
              <div className="space-y-3 text-gray-700">
                <div className="flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-green-600" />
                  <span>Martintar, Nadi (near airport)</span>
                </div>
                <div className="flex items-center gap-2">
                  <Clock className="w-5 h-5 text-green-600" />
                  <span>Open 6:30am - 6pm daily</span>
                </div>
                <div className="flex items-center gap-2">
                  <Coffee className="w-5 h-5 text-green-600" />
                  <span>Best coffee in Nadi!</span>
                </div>
              </div>
              
              <div className="mt-6 p-4 bg-white rounded-lg border-2 border-green-200">
                <p className="font-bold text-green-800">Website</p>
                <p className="text-lg">bulaccinocafe.com</p>
              </div>
            </div>
          </div>

          <div className="mt-8 text-center text-gray-500 text-sm">
            <p>© Bulaccino Cafe - Partner Program Sales Rep Guide</p>
            <p>Questions? Contact your manager.</p>
          </div>
        </div>
      </div>

      {/* Print Styles */}
      <style>{`
        @media print {
          body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          .print\\:hidden { display: none !important; }
          .print\\:break-after-page { break-after: page; }
          .print\\:shadow-none { box-shadow: none !important; }
          .print\\:max-w-none { max-width: none !important; }
        }
      `}</style>
    </div>
  );
}
